import {
  DialogContent,
  DialogContentText,
  TextField,
  MenuItem,
  Box,
  DialogActions,
  Button,
} from "@mui/material";
import { useState } from "react";
import { BaseDialog } from "./base";

export function InputDialog({
  open,
  title,
  description,
  fields = [],
  submitLabel = "Save",
  cancelLabel = "Cancel",
  onSubmit,
  onClose,
}) {
  const [values, setValues] = useState(() => {
    const initialValues = {};
    fields.forEach(field => {
      // Ensure select fields always have a string value, never undefined
      initialValues[field.name] = field.defaultValue !== undefined ? field.defaultValue : '';
    });
    return initialValues;
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (fieldName, value) => {
    setValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const resetValues = () => {
    const resetVals = {};
    fields.forEach(field => {
      resetVals[field.name] = field.defaultValue !== undefined ? field.defaultValue : '';
    });
    return resetVals;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(values);
      setValues(resetValues());
    } catch (error) {
      console.error('Input dialog save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setValues(resetValues());
    onClose();
  };

  const isSubmitDisabled = fields.some(field => 
    field.required && (!values[field.name] || values[field.name].toString().trim() === '')
  );

  return (
    <BaseDialog open={open} onClose={handleClose} title={title}>
      <DialogContent sx={{ pb: 2 }}>
        {description && (
          <DialogContentText sx={{ mb: 2 }}>
            {description}
          </DialogContentText>
        )}
        <Box sx={{ width: '100%', minWidth: 300 }}>
          {fields.map((field) => (
            <Box key={field.name} sx={{ mb: 2 }}>
              {field.type === 'select' ? (
                <TextField
                  select
                  fullWidth
                  label={field.label}
                  value={values[field.name] || ''} // Ensure never undefined
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  required={field.required}
                  size="small"
                >
                  {field.options?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  label={field.label}
                  value={values[field.name] || ''} // Ensure never undefined
                  onChange={(e) => {
                    const val = field.maxLength
                      ? e.target.value.slice(0, field.maxLength)
                      : e.target.value;
                    handleChange(field.name, val);
                  }}
                  multiline={field.multiline}
                  minRows={field.minRows}
                  maxRows={field.maxRows}
                  required={field.required}
                  type={field.type || 'text'}
                  size="small"
                  slotProps={field.maxLength ? {
                    htmlInput: { maxLength: field.maxLength }
                  } : undefined}
                  helperText={field.maxLength
                    ? `${(values[field.name] || '').length} / ${field.maxLength}`
                    : undefined
                  }
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={isSubmitDisabled || loading}
        >
          {loading ? 'Saving...' : submitLabel}
        </Button>
      </DialogActions>
    </BaseDialog>
  );
}